import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, CpuChipIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import AppLayout from '../../layouts/AppLayout';
import apiClient from '../../lib/apiClient';
import './styles.css';

// ─── Componente de progresso ────────────────────────────────────────────────
function StepIndicator({ current }) {
  const steps = ['Detalhes', 'Criativo IA', 'Plano', 'Pagamento'];
  return (
    <div className="cl-step-indicator">
      {steps.map((label, i) => {
        const idx = i + 1;
        const done = idx < current;
        const active = idx === current;
        return (
          <React.Fragment key={idx}>
            <div className={`cl-step ${active ? 'cl-step--active' : ''} ${done ? 'cl-step--done' : ''}`}>
              <div className="cl-step__circle">
                {done ? <CheckIcon style={{width:16,height:16}} /> : idx}
              </div>
              <span className="cl-step__label">{label}</span>
            </div>
            {i < steps.length - 1 && <div className={`cl-step__connector ${done ? 'cl-step__connector--done' : ''}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Animação de carregamento ────────────────────────────────────────────────
function LoadingRobot({ message }) {
  return (
    <div className="cl-loading">
      <div className="cl-loading__robot"><CpuChipIcon style={{width:32,height:32}} /></div>
      <div className="cl-loading__dots">
        <span /><span /><span />
      </div>
      <p className="cl-loading__message">{message}</p>
    </div>
  );
}

// ─── Etapa 1: Formulário de detalhes ────────────────────────────────────────
function Step1({ onNext }) {
  const [form, setForm] = useState({ business_name: '', product_name: '', niche: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (form.business_name.trim().length < 3) e.business_name = 'Mínimo 3 caracteres';
    if (form.product_name.trim().length < 3)  e.product_name  = 'Mínimo 3 caracteres';
    if (form.niche.trim().length < 3)          e.niche         = 'Mínimo 3 caracteres';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (Object.keys(v).length) { setErrors(v); return; }
    setLoading(true);
    try {
      const res = await apiClient.post('/campaign', form);
      onNext({ campaignId: res.data.campaign_id, form });
    } catch (err) {
      setErrors({ global: err.response?.data?.message || 'Erro ao criar campanha' });
    } finally {
      setLoading(false);
    }
  };

  const field = (name, label, placeholder) => (
    <div className="cl-field">
      <label className="cl-field__label">{label}</label>
      <input
        className={`cl-field__input ${errors[name] ? 'cl-field__input--error' : ''}`}
        value={form[name]}
        onChange={e => setForm(p => ({ ...p, [name]: e.target.value }))}
        placeholder={placeholder}
      />
      {errors[name] && <span className="cl-field__error">{errors[name]}</span>}
    </div>
  );

  if (loading) return <LoadingRobot message="Criando sua campanha..." />;

  return (
    <form className="cl-form" onSubmit={handleSubmit}>
      <h2 className="cl-form__title gradient-text">Detalhes da Campanha</h2>
      <p className="cl-form__subtitle">Conte-nos sobre o seu negócio para personalizar a campanha com IA.</p>
      {errors.global && <div className="cl-alert cl-alert--error">{errors.global}</div>}
      {field('business_name', 'Nome do Negócio', 'Ex: Bella Beauty Studio')}
      {field('product_name',  'Produto / Serviço', 'Ex: Curso de Maquiagem Profissional')}
      {field('niche',         'Nicho de Mercado', 'Ex: Beleza e Estética')}
      <button className="neon-button" type="submit">Próximo: Gerar Criativo IA →</button>
    </form>
  );
}

// ─── Etapa 2: Criativo IA ────────────────────────────────────────────────────
function Step2({ campaignId, form, onNext }) {
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [headline, setHeadline] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const generate = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.post('/creative/generate', {
        niche: form.niche,
        product: form.product_name,
        format: 'post',
        style: 'modern',
      });
      const suggestion = res.data?.creative?.copy ||
        res.data?.copy ||
        `Descubra ${form.product_name} — o melhor do segmento ${form.niche}`;
      setGenerated(res.data);
      setHeadline(suggestion);
    } catch {
      setError('Falha ao gerar criativo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [form]);

  const handleNext = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/campaign/${campaignId}`, {
        headline,
        creative_id: generated?.creative?.id || null,
        audience_suggestion: generated?.audience || null,
        step: 2,
      });
      onNext({ headline, audience: generated?.audience });
    } catch {
      setError('Erro ao salvar criativo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingRobot message="Gerando seu criativo com IA..." />;

  return (
    <div className="cl-creative">
      <h2 className="cl-form__title gradient-text">Criativo IA Personalizado</h2>
      <p className="cl-form__subtitle">A IA vai gerar um copy e headline baseado no seu negócio.</p>

      {error && <div className="cl-alert cl-alert--error">{error}</div>}

      {!generated ? (
        <button className="neon-button" onClick={generate}>Gerar Criativo com IA</button>
      ) : (
        <div className="cl-creative__result glass-card">
          <div className="cl-creative__preview">
            <div className="cl-creative__badge neon-badge">IA Gerado</div>
            <textarea
              className="cl-creative__headline"
              value={headline}
              onChange={e => setHeadline(e.target.value)}
              rows={3}
              placeholder="Edite o headline aqui..."
            />
          </div>

          {generated.audience && (
            <div className="cl-creative__audience">
              <strong>Público sugerido pela IA:</strong>
              <p>{typeof generated.audience === 'string'
                ? generated.audience
                : JSON.stringify(generated.audience)}</p>
            </div>
          )}

          <div className="cl-creative__actions">
            <button className="cl-btn cl-btn--ghost" onClick={generate}>↺ Regerar</button>
            <button className="neon-button" onClick={handleNext} disabled={saving}>
              {saving ? 'Salvando...' : 'Próximo: Escolher Plano →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Etapa 3: Planos ────────────────────────────────────────────────────────
function Step3({ campaignId, onNext }) {
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    apiClient.get('/plans')
      .then(r => setPlans(r.data.plans || []))
      .catch(() => setError('Erro ao carregar planos'))
      .finally(() => setLoading(false));
  }, []);

  const handleNext = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await apiClient.put(`/campaign/${campaignId}`, { plan_id: selected, step: 3 });
      onNext({ planId: selected });
    } catch {
      setError('Erro ao salvar plano');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingRobot message="Carregando planos disponíveis..." />;

  const midIndex = Math.floor(plans.length / 2);

  return (
    <div className="cl-plans">
      <h2 className="cl-form__title gradient-text">Escolha seu Plano</h2>
      <p className="cl-form__subtitle">Selecione o plano ideal para sua campanha.</p>
      {error && <div className="cl-alert cl-alert--error">{error}</div>}

      <div className="cl-plans__grid">
        {plans.map((plan, i) => {
          const featured = i === midIndex;
          return (
            <div
              key={plan.id}
              className={`pricing-card ${featured ? 'pricing-card--featured' : ''} ${selected === plan.id ? 'pricing-card--selected' : ''}`}
              onClick={() => setSelected(plan.id)}
            >
              {featured && <div className="pricing-card__badge neon-badge">Mais Popular</div>}
              <h3 className="pricing-card__name">{plan.name}</h3>
              <div className="pricing-card__price">
                <span className="pricing-card__currency">R$</span>
                <span className="pricing-card__amount">{plan.price}</span>
                <span className="pricing-card__period">/mês</span>
              </div>
              <ul className="pricing-card__features">
                {(plan.features || []).map((f, fi) => (
                  <li key={fi}>{f}</li>
                ))}
              </ul>
              <button
                className={selected === plan.id ? 'neon-button' : 'cl-btn cl-btn--outline'}
                onClick={() => setSelected(plan.id)}
              >
                {selected === plan.id ? 'Selecionado' : 'Selecionar'}
              </button>
            </div>
          );
        })}
      </div>

      <div className="cl-plans__action">
        <button className="neon-button" onClick={handleNext} disabled={!selected || saving}>
          {saving ? 'Salvando...' : 'Próximo: Pagamento →'}
        </button>
      </div>
    </div>
  );
}

// ─── Etapa 4: Checkout ───────────────────────────────────────────────────────
function Step4({ campaignId, planId, onDone }) {
  const [state, setState] = useState('idle'); // idle | pending | confirming | done
  const [pixCode, setPixCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const initCheckout = async () => {
    setState('pending');
    setError('');
    try {
      const res = await apiClient.post(`/campaign/${campaignId}/checkout`, { plan_id: planId });
      setPixCode(res.data.pix_code || '');
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao iniciar checkout');
      setState('idle');
    }
  };

  const confirmPayment = async () => {
    setState('confirming');
    try {
      await apiClient.post('/payment/webhook', {
        campaign_id: campaignId,
        event: 'payment.confirmed',
      });
      setState('done');
      setTimeout(() => onDone(), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao confirmar pagamento');
      setState('pending');
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(pixCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (state === 'confirming') return <LoadingRobot message="Confirmando seu pagamento..." />;
  if (state === 'done') return (
    <div className="cl-checkout cl-checkout--success glass-card">
      <div className="cl-checkout__icon"><CheckCircleIcon style={{width:48,height:48,color:'#4ade80'}} /></div>
      <h2 className="gradient-text">Campanha Ativada!</h2>
      <p>Redirecionando para o painel de performance...</p>
    </div>
  );

  return (
    <div className="cl-checkout">
      <h2 className="cl-form__title gradient-text">Pagamento PIX</h2>
      <p className="cl-form__subtitle">Confirme o pagamento para ativar sua campanha.</p>
      {error && <div className="cl-alert cl-alert--error">{error}</div>}

      {state === 'idle' && (
        <div className="cl-checkout__start">
          <div className="cl-checkout__summary glass-card">
            <p>Campanha configurada e criativo gerado</p>
            <p>Plano selecionado</p>
            <p>Aguardando pagamento PIX</p>
          </div>
          <button className="neon-button" onClick={initCheckout}>Gerar Código PIX</button>
        </div>
      )}

      {state === 'pending' && (
        <div className="cl-checkout__pix glass-card">
          <p className="cl-checkout__pix-label">Copie o código PIX abaixo:</p>
          <div className="cl-checkout__pix-code">
            <code>{pixCode}</code>
            <button className="cl-btn cl-btn--ghost" onClick={copy}>
              {copied ? 'Copiado!' : 'Copiar'}
            </button>
          </div>
          <p className="cl-checkout__pix-hint">Após pagar, clique em "Confirmar Pagamento"</p>
          <button className="neon-button" onClick={confirmPayment}>
            Confirmar Pagamento
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Página principal ────────────────────────────────────────────────────────
function CampaignLaunchContent() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [ctx, setCtx] = useState({});

  const handleStep1 = ({ campaignId, form }) => {
    setCtx(p => ({ ...p, campaignId, form }));
    setStep(2);
  };

  const handleStep2 = ({ headline, audience }) => {
    setCtx(p => ({ ...p, headline, audience }));
    setStep(3);
  };

  const handleStep3 = ({ planId }) => {
    setCtx(p => ({ ...p, planId }));
    setStep(4);
  };

  const handleDone = () => {
    navigate(`/campaign/${ctx.campaignId}/dashboard`);
  };

  return (
    <div className="campaign-launch">
      <StepIndicator current={step} />
      <div className="campaign-launch__content glass-card">
        {step === 1 && <Step1 onNext={handleStep1} />}
        {step === 2 && <Step2 campaignId={ctx.campaignId} form={ctx.form} onNext={handleStep2} />}
        {step === 3 && <Step3 campaignId={ctx.campaignId} onNext={handleStep3} />}
        {step === 4 && <Step4 campaignId={ctx.campaignId} planId={ctx.planId} onDone={handleDone} />}
      </div>
    </div>
  );
}

function CampaignLaunchPage() {
  return (
    <AppLayout
      title="Lançar Campanha"
      subtitle="Criação inteligente com IA em 4 etapas"
      fullWidth
    >
      <CampaignLaunchContent />
    </AppLayout>
  );
}

export default CampaignLaunchPage;
